import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authService } from '../services/auth';
import { useAuthStore } from '../store/authStore';

export function useAuth() {
  const navigate = useNavigate();
  const {
    user, isAuthenticated, currentOrganization, pendingMFA, mfaTempToken, pendingTokens,
    login, logout, setUser, setOrganization, hasRole, hasAnyRole,
    setMFAPending, setPendingMFAWithTokens, clearMFA,
  } = useAuthStore();

  const loginMutation = useMutation({
    mutationFn: (credentials) => authService.login(credentials),
    onSuccess: (response) => {
      const { user: userData, access, refresh, tokens, requires_2fa } = response.data;
      const tokenData = tokens || (access && refresh ? { access, refresh } : null);

      if (requires_2fa) {
        const { temp_token, user: partialUser } = response.data;
        setMFAPending(partialUser, temp_token);
        navigate('/dashboard');
        return;
      }

      // Safety net: if backend issued tokens but user has 2FA enabled, hold tokens and show MFA screen
      if (userData?.is_2fa_enabled && tokenData) {
        setPendingMFAWithTokens(userData, tokenData);
        navigate('/dashboard');
        return;
      }

      login(userData, tokenData);
      toast.success(`Welcome back, ${userData.first_name || userData.email}!`);
      navigate('/dashboard');
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Invalid credentials. Please try again.';
      toast.error(message);
    },
  });

  const login2FAMutation = useMutation({
    mutationFn: (data) => authService.login2FA(data),
    onSuccess: (response) => {
      const { user: userData, access, refresh, tokens } = response.data;
      const tokenData = tokens || (access && refresh ? { access, refresh } : null);
      clearMFA();
      login(userData, tokenData);
      toast.success(`Welcome back, ${userData.first_name || userData.email}!`);
    },
    onError: (error) => {
      const message =
        error.response?.data?.otp_code?.[0] ||
        error.response?.data?.temp_token?.[0] ||
        error.response?.data?.detail ||
        'Invalid verification code.';
      toast.error(message);
    },
  });

  const mfaChallengeMutation = useMutation({
    mutationFn: ({ otpCode, accessToken }) => authService.challengeMFA(otpCode, accessToken),
    onSuccess: (response) => {
      const { user: userData, access, refresh, tokens } = response.data;
      const tokenData = tokens || (access && refresh ? { access, refresh } : null);
      clearMFA();
      login(userData, tokenData);
      toast.success(`Welcome back, ${userData.first_name || userData.email}!`);
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Invalid verification code.';
      toast.error(message);
    },
  });

  const registerMutation = useMutation({
    mutationFn: ({ token, data }) => authService.register(token, data),
    onSuccess: (response) => {
      const { user: userData, access, refresh, tokens } = response.data;
      const tokenData = tokens || (access && refresh ? { access, refresh } : null);
      login(userData, tokenData);
      toast.success('Account created successfully! Welcome aboard.');
      navigate('/dashboard');
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => {
      const tokens = JSON.parse(localStorage.getItem('mmpr_tokens') || '{}');
      return authService.logout(tokens.refresh);
    },
    onSettled: () => {
      logout();
      navigate('/login');
      toast.success('You have been logged out.');
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data) => authService.changePassword(data),
    onSuccess: () => {
      toast.success('Password changed successfully.');
    },
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.detail
        || data?.old_password?.[0]
        || data?.new_password?.[0]
        || data?.confirm_password?.[0]
        || data?.non_field_errors?.[0]
        || 'Failed to change password.';
      toast.error(message);
    },
  });

  return {
    user,
    isAuthenticated,
    currentOrganization,
    pendingMFA,
    mfaTempToken,
    pendingTokens,
    hasRole,
    hasAnyRole,
    setUser,
    setOrganization,

    login: loginMutation.mutate,
    isLoggingIn: loginMutation.isPending,

    verify2FA: login2FAMutation.mutate,
    isVerifying2FA: login2FAMutation.isPending,

    challengeMFA: mfaChallengeMutation.mutate,
    isChallengingMFA: mfaChallengeMutation.isPending,

    register: registerMutation.mutate,
    isRegistering: registerMutation.isPending,

    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,

    changePassword: changePasswordMutation.mutate,
    isChangingPassword: changePasswordMutation.isPending,
  };
}
